import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '../lib/api';
import { useAuth } from '../lib/auth';
import { Mail, Phone, MapPin, Calendar, Edit2, Save, X, Eye, EyeOff, Shield, ShieldCheck, ShieldOff, Download } from 'lucide-react';

interface MemberProfile {
  id: string;
  firstName: string;
  lastName: string;
  email: string | null;
  hebrewName: string | null;
  phones: Array<{ type: string; number: string; primary?: boolean }>;
  addressStreet1: string | null;
  addressStreet2: string | null;
  addressCity: string | null;
  addressState: string | null;
  addressPostalCode: string | null;
  dateOfBirth: string | null;
  gender: string | null;
  photoUrl: string | null;
  showInDirectory: boolean;
  directoryFields: Record<string, boolean>;
  membershipType: { id: string; name: string } | null;
  membershipStatus: string;
  household: {
    id: string;
    name: string;
    members: Array<{
      id: string;
      firstName: string;
      lastName: string;
      relationshipToHead: string;
    }>;
  } | null;
}

interface ProfileFormData {
  firstName: string;
  lastName: string;
  email: string;
  hebrewName: string;
  phone: string;
  addressStreet1: string;
  addressStreet2: string;
  addressCity: string;
  addressState: string;
  addressPostalCode: string;
}

interface TwoFactorSetupData {
  secret: string;
  qrCodeUrl: string;
  recoveryCodes: string[];
}

export function ProfilePage() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [isEditing, setIsEditing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // 2FA state
  const [twoFactorSetup, setTwoFactorSetup] = useState<TwoFactorSetupData | null>(null);
  const [twoFactorVerifyCode, setTwoFactorVerifyCode] = useState('');
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(false);
  const [twoFactorLoading, setTwoFactorLoading] = useState(false);
  const [twoFactorError, setTwoFactorError] = useState<string | null>(null);
  const [twoFactorSuccess, setTwoFactorSuccess] = useState<string | null>(null);
  const [showDisablePrompt, setShowDisablePrompt] = useState(false);
  const [disablePassword, setDisablePassword] = useState('');
  const [showRecoveryCodes, setShowRecoveryCodes] = useState(false);

  const { data: profile, isLoading } = useQuery({
    queryKey: ['member', user?.memberId],
    queryFn: async () => {
      if (!user?.memberId) throw new Error('No member ID');
      const response = await api.get<MemberProfile>(`/members/${user.memberId}`);
      return response.data;
    },
    enabled: !!user?.memberId,
  });

  // Check 2FA status
  useQuery({
    queryKey: ['auth-me-2fa'],
    queryFn: async () => {
      const response = await api.get<{ id: string; twoFactorEnabled?: boolean }>('/auth/me');
      if (response.data) {
        setTwoFactorEnabled(!!(response.data as Record<string, unknown>).twoFactorEnabled);
      }
      return response.data;
    },
  });

  const updateMutation = useMutation({
    mutationFn: async (data: Partial<ProfileFormData>) => {
      if (!user?.memberId) throw new Error('No member ID');
      const response = await api.patch<MemberProfile>(`/members/${user.memberId}`, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['member', user?.memberId] });
      setIsEditing(false);
      setError(null);
    },
    onError: (err) => {
      if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError('Failed to update profile');
      }
    },
  });

  const {
    register,
    handleSubmit,
    reset,
  } = useForm<ProfileFormData>();

  const onSubmit = (data: ProfileFormData) => {
    updateMutation.mutate({
      firstName: data.firstName,
      lastName: data.lastName,
      email: data.email || undefined,
      hebrewName: data.hebrewName || undefined,
      addressStreet1: data.addressStreet1 || undefined,
      addressStreet2: data.addressStreet2 || undefined,
      addressCity: data.addressCity || undefined,
      addressState: data.addressState || undefined,
      addressPostalCode: data.addressPostalCode || undefined,
    });
  };

  const startEditing = () => {
    if (profile) {
      reset({
        firstName: profile.firstName,
        lastName: profile.lastName,
        email: profile.email || '',
        hebrewName: profile.hebrewName || '',
        phone: profile.phones?.[0]?.number || '',
        addressStreet1: profile.addressStreet1 || '',
        addressStreet2: profile.addressStreet2 || '',
        addressCity: profile.addressCity || '',
        addressState: profile.addressState || '',
        addressPostalCode: profile.addressPostalCode || '',
      });
    }
    setIsEditing(true);
  };

  const handleSetup2FA = async () => {
    setTwoFactorLoading(true);
    setTwoFactorError(null);
    setTwoFactorSuccess(null);

    try {
      const response = await api.post<TwoFactorSetupData>('/auth/2fa/setup');
      if (response.success && response.data) {
        setTwoFactorSetup(response.data);
      }
    } catch (err) {
      if (err instanceof ApiError) {
        setTwoFactorError(err.message);
      } else {
        setTwoFactorError('Failed to set up two-factor authentication');
      }
    } finally {
      setTwoFactorLoading(false);
    }
  };

  const handleVerify2FA = async () => {
    setTwoFactorLoading(true);
    setTwoFactorError(null);

    try {
      const response = await api.post<{ message: string }>('/auth/2fa/verify', {
        code: twoFactorVerifyCode,
      });
      if (response.success) {
        setTwoFactorEnabled(true);
        setTwoFactorVerifyCode('');
        setShowRecoveryCodes(true);
        setTwoFactorSuccess('Two-factor authentication has been enabled.');
      }
    } catch (err) {
      if (err instanceof ApiError) {
        setTwoFactorError(err.message);
      } else {
        setTwoFactorError('Invalid verification code');
      }
    } finally {
      setTwoFactorLoading(false);
    }
  };

  const handleDisable2FA = async () => {
    setTwoFactorLoading(true);
    setTwoFactorError(null);

    try {
      const response = await api.post<{ message: string }>('/auth/2fa/disable', {
        password: disablePassword,
      });
      if (response.success) {
        setTwoFactorEnabled(false);
        setTwoFactorSetup(null);
        setShowDisablePrompt(false);
        setDisablePassword('');
        setTwoFactorSuccess('Two-factor authentication has been disabled.');
      }
    } catch (err) {
      if (err instanceof ApiError) {
        setTwoFactorError(err.message);
      } else {
        setTwoFactorError('Failed to disable two-factor authentication');
      }
    } finally {
      setTwoFactorLoading(false);
    }
  };

  const downloadRecoveryCodes = () => {
    if (!twoFactorSetup?.recoveryCodes) return;
    const content = [
      'OpenShul Recovery Codes',
      '========================',
      '',
      'Store these codes in a safe place. Each code can only be used once.',
      '',
      ...twoFactorSetup.recoveryCodes.map((code, i) => `${i + 1}. ${code}`),
      '',
      `Generated: ${new Date().toISOString()}`,
    ].join('\n');

    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'openshul-recovery-codes.txt';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-600 border-t-transparent" />
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Unable to load profile</p>
      </div>
    );
  }

  const primaryPhone = profile.phones?.find((p) => p.primary) || profile.phones?.[0];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">My Profile</h1>
        {!isEditing && (
          <button
            onClick={startEditing}
            className="flex items-center gap-2 rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700"
          >
            <Edit2 className="h-4 w-4" />
            Edit Profile
          </button>
        )}
      </div>

      {error && (
        <div className="rounded-lg bg-red-50 p-4 text-sm text-red-700">{error}</div>
      )}

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Profile Card */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="text-center">
            {profile.photoUrl ? (
              <img
                src={profile.photoUrl}
                alt=""
                className="mx-auto h-24 w-24 rounded-full object-cover"
              />
            ) : (
              <div className="mx-auto flex h-24 w-24 items-center justify-center rounded-full bg-primary-100 text-primary-600 text-3xl font-bold">
                {profile.firstName.charAt(0)}
                {profile.lastName.charAt(0)}
              </div>
            )}
            <h2 className="mt-4 text-xl font-semibold text-gray-900">
              {profile.firstName} {profile.lastName}
            </h2>
            {profile.hebrewName && (
              <p className="text-gray-600" dir="rtl">
                {profile.hebrewName}
              </p>
            )}
            <div className="mt-2">
              <span
                className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
                  profile.membershipStatus === 'active'
                    ? 'bg-green-100 text-green-800'
                    : profile.membershipStatus === 'pending'
                    ? 'bg-yellow-100 text-yellow-800'
                    : 'bg-gray-100 text-gray-800'
                }`}
              >
                {profile.membershipStatus.charAt(0).toUpperCase() + profile.membershipStatus.slice(1)}
              </span>
            </div>
            {profile.membershipType && (
              <p className="mt-2 text-sm text-gray-500">{profile.membershipType.name}</p>
            )}
          </div>

          {/* Directory Visibility */}
          <div className="mt-6 pt-6 border-t border-gray-100">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Show in Directory</span>
              <span className="flex items-center gap-1">
                {profile.showInDirectory ? (
                  <>
                    <Eye className="h-4 w-4 text-green-600" />
                    <span className="text-sm text-green-600">Visible</span>
                  </>
                ) : (
                  <>
                    <EyeOff className="h-4 w-4 text-gray-400" />
                    <span className="text-sm text-gray-400">Hidden</span>
                  </>
                )}
              </span>
            </div>
          </div>
        </div>

        {/* Details */}
        <div className="lg:col-span-2 space-y-6">
          {isEditing ? (
            <form onSubmit={handleSubmit(onSubmit)} className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-gray-900">Edit Information</h3>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setIsEditing(false)}
                    className="flex items-center gap-1 rounded-lg border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
                  >
                    <X className="h-4 w-4" />
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={updateMutation.isPending}
                    className="flex items-center gap-1 rounded-lg bg-primary-600 px-3 py-1.5 text-sm text-white hover:bg-primary-700 disabled:opacity-50"
                  >
                    <Save className="h-4 w-4" />
                    {updateMutation.isPending ? 'Saving...' : 'Save'}
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label className="block text-sm font-medium text-gray-700">First Name</label>
                  <input
                    {...register('firstName', { required: true })}
                    className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Last Name</label>
                  <input
                    {...register('lastName', { required: true })}
                    className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Hebrew Name</label>
                  <input
                    {...register('hebrewName')}
                    dir="rtl"
                    className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Email</label>
                  <input
                    {...register('email')}
                    type="email"
                    className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-sm font-medium text-gray-700">Street Address</label>
                  <input
                    {...register('addressStreet1')}
                    className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-sm font-medium text-gray-700">Address Line 2</label>
                  <input
                    {...register('addressStreet2')}
                    className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">City</label>
                  <input
                    {...register('addressCity')}
                    className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">State</label>
                  <input
                    {...register('addressState')}
                    className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Postal Code</label>
                  <input
                    {...register('addressPostalCode')}
                    className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                  />
                </div>
              </div>
            </form>
          ) : (
            <>
              {/* Contact Information */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Contact Information</h3>
                <div className="space-y-4">
                  {profile.email && (
                    <div className="flex items-center gap-3">
                      <Mail className="h-5 w-5 text-gray-400" />
                      <span className="text-gray-700">{profile.email}</span>
                    </div>
                  )}
                  {primaryPhone && (
                    <div className="flex items-center gap-3">
                      <Phone className="h-5 w-5 text-gray-400" />
                      <span className="text-gray-700">{primaryPhone.number}</span>
                      <span className="text-xs text-gray-400 capitalize">({primaryPhone.type})</span>
                    </div>
                  )}
                  {profile.addressStreet1 && (
                    <div className="flex items-start gap-3">
                      <MapPin className="h-5 w-5 text-gray-400 mt-0.5" />
                      <div className="text-gray-700">
                        <p>{profile.addressStreet1}</p>
                        {profile.addressStreet2 && <p>{profile.addressStreet2}</p>}
                        <p>
                          {profile.addressCity}, {profile.addressState} {profile.addressPostalCode}
                        </p>
                      </div>
                    </div>
                  )}
                  {profile.dateOfBirth && (
                    <div className="flex items-center gap-3">
                      <Calendar className="h-5 w-5 text-gray-400" />
                      <span className="text-gray-700">
                        {new Date(profile.dateOfBirth).toLocaleDateString()}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* Security - Two-Factor Authentication */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <Shield className="h-5 w-5 text-gray-700" />
                  Security
                </h3>

                {twoFactorError && (
                  <div className="rounded-lg bg-red-50 p-3 text-sm text-red-700 mb-4">
                    {twoFactorError}
                  </div>
                )}

                {twoFactorSuccess && (
                  <div className="rounded-lg bg-green-50 p-3 text-sm text-green-700 mb-4">
                    {twoFactorSuccess}
                  </div>
                )}

                {twoFactorEnabled && !twoFactorSetup ? (
                  /* 2FA is enabled */
                  <div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <ShieldCheck className="h-5 w-5 text-green-600" />
                        <span className="text-sm font-medium text-green-700">
                          Two-Factor Authentication Enabled
                        </span>
                      </div>
                      <button
                        onClick={() => {
                          setShowDisablePrompt(true);
                          setTwoFactorError(null);
                          setTwoFactorSuccess(null);
                        }}
                        className="flex items-center gap-1 rounded-lg border border-red-300 px-3 py-1.5 text-sm text-red-600 hover:bg-red-50"
                      >
                        <ShieldOff className="h-4 w-4" />
                        Disable
                      </button>
                    </div>

                    {showDisablePrompt && (
                      <div className="mt-4 p-4 rounded-lg bg-gray-50 border border-gray-200">
                        <p className="text-sm text-gray-700 mb-3">
                          Enter your password to disable two-factor authentication.
                        </p>
                        <div className="flex gap-2">
                          <input
                            type="password"
                            placeholder="Your password"
                            value={disablePassword}
                            onChange={(e) => setDisablePassword(e.target.value)}
                            className="flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                          />
                          <button
                            onClick={handleDisable2FA}
                            disabled={twoFactorLoading || !disablePassword}
                            className="rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
                          >
                            {twoFactorLoading ? 'Disabling...' : 'Confirm'}
                          </button>
                          <button
                            onClick={() => {
                              setShowDisablePrompt(false);
                              setDisablePassword('');
                              setTwoFactorError(null);
                            }}
                            className="rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ) : twoFactorSetup ? (
                  /* Setup in progress */
                  <div className="space-y-4">
                    {!showRecoveryCodes ? (
                      <>
                        <p className="text-sm text-gray-600">
                          Scan this QR code with your authenticator app (Google Authenticator, Authy, etc.),
                          then enter the 6-digit code to verify.
                        </p>

                        <div className="flex justify-center">
                          <img
                            src={twoFactorSetup.qrCodeUrl}
                            alt="Two-Factor Authentication QR Code"
                            className="h-48 w-48"
                          />
                        </div>

                        <div className="text-center">
                          <p className="text-xs text-gray-500 mb-1">
                            Or enter this secret manually:
                          </p>
                          <code className="text-xs bg-gray-100 px-2 py-1 rounded font-mono select-all break-all">
                            {twoFactorSetup.secret}
                          </code>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Verification Code
                          </label>
                          <div className="flex gap-2">
                            <input
                              type="text"
                              placeholder="000000"
                              value={twoFactorVerifyCode}
                              onChange={(e) => setTwoFactorVerifyCode(e.target.value)}
                              maxLength={6}
                              className="flex-1 rounded-lg border border-gray-300 px-3 py-2 text-center text-lg tracking-widest focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                            />
                            <button
                              onClick={handleVerify2FA}
                              disabled={twoFactorLoading || twoFactorVerifyCode.length !== 6}
                              className="rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50"
                            >
                              {twoFactorLoading ? 'Verifying...' : 'Verify'}
                            </button>
                          </div>
                        </div>

                        <button
                          onClick={() => {
                            setTwoFactorSetup(null);
                            setTwoFactorVerifyCode('');
                            setTwoFactorError(null);
                          }}
                          className="text-sm text-gray-500 hover:text-gray-700"
                        >
                          Cancel setup
                        </button>
                      </>
                    ) : (
                      /* Show recovery codes after successful verification */
                      <>
                        <div className="rounded-lg bg-amber-50 border border-amber-200 p-4">
                          <p className="text-sm font-medium text-amber-800 mb-2">
                            Save your recovery codes
                          </p>
                          <p className="text-sm text-amber-700">
                            Store these codes in a safe place. If you lose access to your authenticator app,
                            you can use a recovery code to sign in. Each code can only be used once.
                          </p>
                        </div>

                        <div className="bg-gray-50 rounded-lg p-4">
                          <div className="grid grid-cols-2 gap-2">
                            {twoFactorSetup.recoveryCodes.map((code, index) => (
                              <code
                                key={index}
                                className="text-sm font-mono bg-white px-2 py-1 rounded border border-gray-200 text-center"
                              >
                                {code}
                              </code>
                            ))}
                          </div>
                        </div>

                        <div className="flex gap-2">
                          <button
                            onClick={downloadRecoveryCodes}
                            className="flex items-center gap-2 rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                          >
                            <Download className="h-4 w-4" />
                            Download Codes
                          </button>
                          <button
                            onClick={() => {
                              setTwoFactorSetup(null);
                              setShowRecoveryCodes(false);
                            }}
                            className="rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700"
                          >
                            Done
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                ) : (
                  /* 2FA not enabled */
                  <div>
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-gray-700">Two-Factor Authentication</p>
                        <p className="text-xs text-gray-500 mt-1">
                          Add an extra layer of security to your account by requiring a code from your
                          authenticator app when signing in.
                        </p>
                      </div>
                      <button
                        onClick={handleSetup2FA}
                        disabled={twoFactorLoading}
                        className="flex items-center gap-2 rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50 whitespace-nowrap"
                      >
                        <Shield className="h-4 w-4" />
                        {twoFactorLoading ? 'Setting up...' : 'Enable 2FA'}
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* Household */}
              {profile.household && (
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Household</h3>
                  <p className="text-gray-600 mb-4">{profile.household.name}</p>
                  <div className="space-y-3">
                    {profile.household.members.map((member) => (
                      <div
                        key={member.id}
                        className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-50"
                      >
                        <div className="flex h-8 w-8 items-center justify-center rounded-full bg-gray-100 text-gray-600 text-sm font-medium">
                          {member.firstName.charAt(0)}
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-900">
                            {member.firstName} {member.lastName}
                          </p>
                          <p className="text-xs text-gray-500 capitalize">
                            {member.relationshipToHead?.replace('_', ' ')}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
